import { Injectable, inject, signal } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel
} from '@microsoft/signalr';

import { AuthService } from './auth.service';
import { TokenBagService } from './token-bag.service';
import { environment } from '../../../environments/environment';
import { resolveTenantBaseUrl } from './your-events.service';
import type { LiveKitCredentials } from '../models/video.models';

/**
 * SignalR client for the Engagement service's `VideoHub` (`/hubs/video`) —
 * the mobile counterpart of `apps/web/src/app/core/services/video-hub.service.ts`.
 *
 * `VideoHub` mints LiveKit access tokens (it never proxies media): the
 * mobile app connects raw `livekit-client` straight to LiveKit Cloud using
 * the `{ token, serverUrl }` this service returns. Two flows use it:
 *
 *  - **Live booth rooms** — `joinBoothRoom(boothId)` returns role-scoped
 *    credentials (reps publish; attendees publish only when the booth has
 *    attendee-video enabled, otherwise listen-only).
 *  - **Networking 1:1 calls** — `joinPrivateCallRoom(roomName)` returns
 *    full-publish credentials for both parties of a private call. Call
 *    *signalling* (ring/accept/decline) rides `EngagementHubService`; this
 *    hub only hands out the room credentials.
 *
 * The connection is opened lazily on first use (a video feature is opt-in,
 * so we don't hold a socket open for users who never start a call) and is
 * rebuilt against the active tenant's bearer whenever the tenant changes.
 * Unlike the always-on EngagementHub, this hub stays dormant until a video
 * surface asks for credentials.
 */
const HUB_PATH = '/hubs/video';
const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000, 30_000];
const CONNECT_TIMEOUT_MS = 12_000;

@Injectable({ providedIn: 'root' })
export class VideoHubService {
  private readonly auth = inject(AuthService);
  private readonly tokenBag = inject(TokenBagService);

  private connection: HubConnection | null = null;
  private connectedTenantId: number | null = null;
  private startInFlight: Promise<void> | null = null;

  private readonly _isConnected = signal(false);
  /** True while the VideoHub socket is open. */
  readonly isConnected = this._isConnected.asReadonly();

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------

  /**
   * Fetch LiveKit credentials for a booth's live video room. The backend
   * keys permissions off the caller's role + the booth's attendee-video
   * setting, so `canPublish` may come back false (listen-only).
   */
  async joinBoothRoom(boothId: number): Promise<LiveKitCredentials> {
    const conn = await this.getConnectedConnection();
    const raw = await conn.invoke<unknown>('JoinBoothRoom', boothId);
    return this.mapCredentials(raw);
  }

  /** Tell the hub we left a booth room (best-effort; frees the SignalR group). */
  async leaveBoothRoom(boothId: number): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('LeaveBoothRoom', boothId);
    } catch (err) {
      console.warn('[VideoHubService] LeaveBoothRoom failed:', err);
    }
  }

  /**
   * Fetch LiveKit credentials for a 1:1 private call room. Both parties get
   * full publish permissions. `roomName` must be the deterministic
   * `private-{a}-{b}` name both sides agreed on (see `privateRoomName`).
   */
  async joinPrivateCallRoom(roomName: string): Promise<LiveKitCredentials> {
    const conn = await this.getConnectedConnection();
    const raw = await conn.invoke<unknown>('JoinPrivateCallRoom', roomName);
    return this.mapCredentials(raw, roomName);
  }

  /** Tear the socket down (e.g. on logout). Idempotent. */
  async disconnect(): Promise<void> {
    const conn = this.connection;
    this.connection = null;
    this.connectedTenantId = null;
    this.startInFlight = null;
    this._isConnected.set(false);
    if (!conn) return;
    try {
      await conn.stop();
    } catch {
      // Idempotent.
    }
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  private async getConnectedConnection(): Promise<HubConnection> {
    const active = this.tokenBag.getActive();
    if (!active) {
      throw new Error('Not signed in — video is unavailable.');
    }

    // Rebuild against the active tenant's bearer when the tenant changed
    // (or on first use). A live socket on the right tenant is reused.
    if (
      this.connection &&
      this.connectedTenantId === active.tenantId &&
      this.connection.state === HubConnectionState.Connected
    ) {
      return this.connection;
    }

    if (this.connection && this.connectedTenantId !== active.tenantId) {
      await this.disconnect();
    }

    if (!this.connection) {
      const baseUrl = resolveTenantBaseUrl(environment.apiUrl, active.tenantSlug);
      this.connection = new HubConnectionBuilder()
        .withUrl(`${baseUrl}${HUB_PATH}`, {
          accessTokenFactory: () => this.auth.getToken() ?? '',
          transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (ctx) =>
            RECONNECT_DELAYS_MS[Math.min(ctx.previousRetryCount, RECONNECT_DELAYS_MS.length - 1)]
        })
        .configureLogging(environment.production ? LogLevel.Warning : LogLevel.Information)
        .build();
      this.connectedTenantId = active.tenantId;
      this.connection.onreconnected(() => this._isConnected.set(true));
      this.connection.onreconnecting(() => this._isConnected.set(false));
      this.connection.onclose(() => this._isConnected.set(false));
    }

    await this.ensureStarted();
    return this.connection;
  }

  /** Start the connection once, coalescing concurrent callers. */
  private async ensureStarted(): Promise<void> {
    const conn = this.connection;
    if (!conn) throw new Error('VideoHub connection not initialised.');
    if (conn.state === HubConnectionState.Connected) return;
    if (this.startInFlight) return this.startInFlight;

    this.startInFlight = (async () => {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timed out connecting to the video service.')), CONNECT_TIMEOUT_MS)
      );
      try {
        await Promise.race([conn.start(), timeout]);
        this._isConnected.set(true);
      } finally {
        this.startInFlight = null;
      }
    })();
    return this.startInFlight;
  }

  /**
   * Normalise the hub response to camelCase. `LiveKitTokenService` returns
   * PascalCase keys, but Azure SignalR / older builds may surface camelCase,
   * so accept both — mirrors the web client's defensive mapping.
   */
  private mapCredentials(raw: unknown, fallbackRoom?: string): LiveKitCredentials {
    if (!raw || typeof raw !== 'object') {
      throw new Error('Video service returned an empty response.');
    }
    const r = raw as Record<string, unknown>;
    const token = pickString(r['token'], r['Token']);
    const serverUrl = pickString(r['serverUrl'], r['ServerUrl']);
    const roomName = pickString(r['roomName'], r['RoomName']) ?? fallbackRoom;
    if (!token || !serverUrl || !roomName) {
      throw new Error('Video service returned incomplete credentials.');
    }
    return {
      token,
      serverUrl,
      roomName,
      canPublish: pickBool(r['canPublish'], r['CanPublish']),
      networkingMode: pickBool(r['networkingMode'], r['NetworkingMode'])
    };
  }
}

function pickString(...candidates: unknown[]): string | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  }
  return undefined;
}

function pickBool(...candidates: unknown[]): boolean {
  for (const candidate of candidates) {
    if (typeof candidate === 'boolean') return candidate;
  }
  return false;
}
